import { useTranslation } from "react-i18next"
import { Tag } from "@/components/ui/tag"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { EMPTY_VALUE_PLACEHOLDER } from "@/modules/admin/better-doc/constants/better-doc-constants"
import {
  formatBetterDocRecommendationState,
  getBetterDocEnumLabel,
} from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { formatBetterDocDateTime, formatBetterDocValue } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocSessionSummary } from "@/modules/admin/better-doc/services/betterDocSessionQuery"

export type BetterDocSessionTableRow = Pick<
  BetterDocSessionSummary,
  | "sessionId"
  | "partnerSessionId"
  | "partnerPatientId"
  | "progressNoteId"
  | "isTestCase"
  | "testCaseScenario"
  | "navigationType"
  | "insuranceCountry"
  | "betterDocState"
  | "createdAt"
  | "createdBy"
>

type BetterDocSessionTableProps = {
  sessions: ReadonlyArray<BetterDocSessionTableRow>
}

export const BetterDocSessionTable = ({ sessions }: BetterDocSessionTableProps) => {
  const { t } = useTranslation()

  return (
    <BetterDocTable data-testid="better-doc-admin-session-table">
      <thead>
        <tr>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.session")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.partnerSessionId")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.partnerPatientId")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.progressNoteId")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.kind")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.navigationType")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.insuranceCountry")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.betterDocState")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.sessionTable.created")}</BetterDocTableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {sessions.map((session) => (
          <tr key={session.sessionId} className="hover:bg-surface-hovered" data-testid="better-doc-admin-session-row">
            <BetterDocTableCell>
              <BetterDocSessionLink sessionId={session.sessionId} />
            </BetterDocTableCell>
            <BetterDocTableCell className="break-all">{session.partnerSessionId}</BetterDocTableCell>
            <BetterDocTableCell className="break-all">{session.partnerPatientId}</BetterDocTableCell>
            <BetterDocTableCell>{formatBetterDocValue(session.progressNoteId)}</BetterDocTableCell>
            <BetterDocTableCell>
              {session.isTestCase ? (
                <div className="flex flex-wrap items-center gap-1">
                  <Tag type="attention" hasIcon={false} label={t("admin.betterDoc.testCaseTag")} />
                  {session.testCaseScenario === null ? null : (
                    <span>{getBetterDocEnumLabel(t, "testCaseScenario", session.testCaseScenario)}</span>
                  )}
                </div>
              ) : (
                EMPTY_VALUE_PLACEHOLDER
              )}
            </BetterDocTableCell>
            <BetterDocTableCell>
              {session.navigationType === null
                ? EMPTY_VALUE_PLACEHOLDER
                : getBetterDocEnumLabel(t, "navigationType", session.navigationType)}
            </BetterDocTableCell>
            <BetterDocTableCell>
              {session.insuranceCountry === null
                ? EMPTY_VALUE_PLACEHOLDER
                : getBetterDocEnumLabel(t, "insuranceCountry", session.insuranceCountry)}
            </BetterDocTableCell>
            <BetterDocTableCell>{formatBetterDocRecommendationState(t, session.betterDocState)}</BetterDocTableCell>
            <BetterDocTableCell>
              <div className="flex flex-col">
                <span className="whitespace-nowrap">{formatBetterDocDateTime(session.createdAt)}</span>
                <span className="text-on-surface-subdued text-xs">{session.createdBy}</span>
              </div>
            </BetterDocTableCell>
          </tr>
        ))}
      </tbody>
    </BetterDocTable>
  )
}
